import React, { useState, useEffect, useRef } from 'react';
import { MdSend, MdClose, MdArrowDownward } from 'react-icons/md';
import './ChatPanel.css';

const ChatPanel = ({
  messages,
  typingUsers,
  onSendMessage,
  onTyping,
  onClose,
  currentUsername
}) => {
  const [inputText, setInputText] = useState('');
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [showNewMessageBanner, setShowNewMessageBanner] = useState(false);
  
  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Scroll handler to determine if user is near bottom
  const handleScroll = () => {
    if (!chatMessagesRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatMessagesRef.current;
    
    // Consider "near bottom" if within 100px of the bottom
    const isBottom = scrollHeight - scrollTop - clientHeight < 100;
    setIsNearBottom(isBottom);

    if (isBottom && showNewMessageBanner) {
      setShowNewMessageBanner(false);
    }
  };

  // Effect to handle new messages scroll
  useEffect(() => {
    if (isNearBottom) {
      scrollToBottom();
    } else {
      // If a new message arrived and we aren't near the bottom, show the banner
      // (We assume messages prop changed, which triggered this effect)
      if (messages.length > 0) {
        // Only show if the last message isn't from us
        const lastMsg = messages[messages.length - 1];
        if (lastMsg.username !== currentUsername && lastMsg.type !== 'system') {
          setShowNewMessageBanner(true);
        } else if (lastMsg.username === currentUsername) {
          // Always scroll to our own messages
          scrollToBottom();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setIsNearBottom(true);
    setShowNewMessageBanner(false);
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    
    // Trigger typing event
    onTyping(true);

    // Debounce stop typing
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      onTyping(false);
    }, 2000);
  };

  const handleSend = () => {
    const trimmed = inputText.trim();
    if (!trimmed) return;
    
    if (trimmed.length > 1000) {
      return; // Handled by server or we could add local toast, but server will reject anyway
    }

    onSendMessage(trimmed);
    setInputText('');
    
    // Immediately stop typing
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    onTyping(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Format typing text
  const getTypingText = () => {
    if (typingUsers.length === 0) return '';
    if (typingUsers.length === 1) return `${typingUsers[0]} is typing...`;
    if (typingUsers.length === 2) return `${typingUsers[0]} and ${typingUsers[1]} are typing...`;
    return `${typingUsers.length} people are typing...`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="chatPanel">
      <div className="chatHeader">
        <h3>Room Chat</h3>
        <button className="btn iconBtn closeBtn" onClick={onClose} title="Close Chat">
          <MdClose size={20} />
        </button>
      </div>

      <div className="chatMessages" ref={chatMessagesRef} onScroll={handleScroll}>
        {messages.map((msg, index) => {
          if (msg.type === 'system') {
            return (
              <div key={msg.id || index} className="systemMessage">
                {msg.message}
              </div>
            );
          }

          const isOwn = msg.username === currentUsername;
          return (
            <div key={msg.id || index} className={`messageWrapper ${isOwn ? 'own' : 'other'}`}>
              <div className="messageHeader">
                <span className="messageSender">{isOwn ? 'You' : msg.username}</span>
                <span className="messageTime">{formatTime(msg.createdAt)}</span>
              </div>
              <div className="messageBubble">
                {msg.message}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
        
        {showNewMessageBanner && (
          <button className="newMessagesBanner" onClick={scrollToBottom}>
            <MdArrowDownward size={16} />
            New Messages
          </button>
        )}
      </div>

      <div className="chatInputArea">
        <div className="typingIndicatorBar">
          {getTypingText()}
        </div>
        <div className="chatInputWrapper">
          <textarea
            className="chatInput"
            placeholder="Type a message..."
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            maxLength={1000}
            rows={1}
          />
          <button 
            className="sendBtn" 
            onClick={handleSend}
            disabled={!inputText.trim()}
            title="Send"
          >
            <MdSend size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
